<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

import ButtonAddToCart from '@/components/CartActions/ButtonAddToCart.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import DropdownSelector from '@/shared/components/UI/DropdownSelector.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { OptionType } from '@/shared/components/UI/ui.types';
import {
  PatternProduct,
  Variant
} from '@/shared/composables/patternProducts/patternProducts.types';
import { usePatternProducts } from '@/shared/composables/patternProducts/usePatternProducts';
import { useTranslations } from '@/shared/composables/useTranslations';
import { productTypes } from '@/shared/constants/shopify';
import { useCartStore } from '@/shared/pinia/modules/cart.ts';
import { useConfigStore } from '@/shared/pinia/modules/config';
import { getErrorMessage } from '@/shared/utils/error';
import { shopifyGidToId } from '@/shared/utils/shopify/id-formatter';
import { formatPriceStorefront } from '@/shared/utils/shopify/money-formatter';
import { trackUserEvent, UserEventDefinitions } from '@/shared/utils/tracking';

import PatternProductCard from './PatternProductCard.vue';
import PatternProductSkeleton from './PatternProductSkeleton.vue';

const { t } = useTranslations();
const cartStore = useCartStore();
const configStore = useConfigStore();
const buttonAddAllToCartRef = useTemplateRef('ButtonAddAllToCart');

const size = ref<string | null | undefined>(null);
const isAddingAllToCart = ref<boolean>(false);
const displayWarning = ref<boolean>(false);
const productCardsRef = ref<(typeof PatternProductCard)[]>([]);

const { patternId, defaultPatternProductsList } = defineProps<{
  patternId: string;
  defaultPatternProductsList: PatternProduct[];
}>();

const {
  hydratedProductList,
  patternVariants,
  getProductSubtitle,
  getPrices,
  getDiscountPercentage
} = usePatternProducts(patternId, defaultPatternProductsList);

const patternProducts = ref<PatternProduct[]>(defaultPatternProductsList);

const addAllToCartLabel = computed(() => {
  const totalPrice = hydratedPatternProductsList.value.reduce((total, hpp) => {
    const inStock = (hpp.product?.variant?.quantityAvailable || 0) > 0;
    if (inStock) {
      const unitPrice = hpp.product ? hpp.product.price.amount : 0;
      const currentPPValues = patternProducts.value.find(
        (currentPP) =>
          currentPP.product_id == hpp.product?.id &&
          currentPP.variant_id == hpp.product?.variant?.id
      );
      total = total + unitPrice * (currentPPValues?.quantity || 1);
    }
    return total;
  }, 0);

  return t('patterns.products.add-all-to-cart', {
    price: formatPriceStorefront({
      amount: totalPrice,
      currencyCode: window.Shopify.currency.active
    })
  });
});

const sizeOptions = computed(() => {
  return patternVariants.value
    .filter((a) => a)
    .map((pv) => {
      return { value: pv.title || '', label: pv.title || '' };
    });
});

const filterTypeOptions = computed(() => {
  return [
    {
      value: 'all',
      label: t('patterns.products.show-me-everything')
    },
    {
      value: productTypes.YARN,
      label: t('patterns.products.show-me-yarn')
    }
  ];
});

const isThereAnyInsufficientStock = computed(() => {
  return hydratedPatternProductsList.value.reduce(
    (anyInsufficientStock, patternProduct) => {
      return (
        anyInsufficientStock ||
        (patternProduct.product?.variant?.quantityAvailable || 0) <
          patternProduct.quantity
      );
    },
    false
  );
});
const isEverythingOutOfStock = computed(() => {
  return hydratedPatternProductsList.value.reduce(
    (allOutOfStock, patternProduct) =>
      allOutOfStock && patternProduct.product?.variant?.quantityAvailable === 0,
    true
  );
});

watch(patternVariants, (newVal) => {
  if (newVal.length) {
    size.value = newVal[0].title;
  }
});

const onProductChange = ({
  productId,
  variantId,
  quantity,
  index
}: {
  productId: string;
  variantId: string;
  quantity: number;
  index: number;
}) => {
  patternProducts.value = patternProducts.value.map((pp, i) => {
    if (i === index && pp.product_id === productId) {
      return {
        product_id: pp.product_id,
        variant_id: variantId,
        quantity
      };
    }

    return pp;
  });
};

const getVariantWeight = (variant: Variant, productType: string) => {
  const metafield = variant.metafield?.value;

  if (metafield && productType === 'yarn') {
    const dimensions = JSON.parse(metafield);
    return `${dimensions.weight}g`;
  }

  return '';
};

const hydratedPatternProductsList = computed(() => {
  return patternProducts.value
    ?.map((pp) => {
      const hydratedProduct = hydratedProductList?.value?.find(
        (p) => p.id === pp.product_id
      );

      if (!hydratedProduct) return pp;

      const selectedVariant = hydratedProduct.variants.nodes.find(
        (v) => v.id === pp.variant_id
      );

      const variant =
        selectedVariant || hydratedProduct.variants.nodes[0] || null;
      const variantId = shopifyGidToId(variant?.id);
      const productId = shopifyGidToId(hydratedProduct?.id);

      const { price, compareAtPrice } = getPrices(variant, {
        price: hydratedProduct.priceRange,
        compareAtPrice: hydratedProduct.compareAtPriceRange
      });

      return {
        ...pp,
        product: {
          id: hydratedProduct.id,
          idNumeric: productId,
          title: hydratedProduct.title,
          handle: hydratedProduct.handle,
          brand: hydratedProduct.metafields
            .filter((a) => a)
            .find((m) => m.key === 'brand' || m.key === 'pattern_kind')?.value,
          subtitle: getProductSubtitle(hydratedProduct.metafields),
          image: hydratedProduct.featuredImage,
          type: hydratedProduct.productType,
          price,
          compareAtPrice,
          points: configStore.calculatedPoints(price.amount * 100),
          discountPercentage: getDiscountPercentage(
            variant.price,
            variant.compareAtPrice
          ),
          variant: {
            ...variant,
            weight: getVariantWeight(variant, hydratedProduct.productType),
            idNumeric: variantId
          },
          variants: hydratedProduct.variants
        }
      };
    })
    .filter((a) => {
      const filterIn = showingOnlyYarn.value
        ? a.product?.type === productTypes.YARN
        : true;
      return !!a.product && filterIn && a.quantity > 0;
    });
});

const showingOnlyYarn = ref<boolean>(false);

const handleProductTypeChange = (newSelection: OptionType) => {
  showingOnlyYarn.value = newSelection.value === productTypes.YARN;
};

const handleSizeChange = (newSize: OptionType) => {
  size.value = newSize.value;

  const selectedPatternVariant = patternVariants.value?.find(
    (pv) => pv.title === size.value
  );

  const ppMetafield = selectedPatternVariant?.metafield?.value;

  if (ppMetafield) {
    const newMetafield: PatternProduct[] = JSON.parse(ppMetafield);

    const newQuantities = newMetafield.reduce<Record<string, number>>(
      (acc, newPP) => {
        acc[newPP.product_id] = newPP.quantity ?? 1;
        return acc;
      },
      {}
    );

    patternProducts.value = patternProducts.value.map((pp) => {
      const updatedQuantity = newQuantities[pp.product_id];

      return {
        ...pp,
        quantity: updatedQuantity || pp.quantity
      };
    });
  }
};

const trackAddAllToCart = (
  items: UserEventDefinitions['pattern_products_add_all_to_cart']['items']
) => {
  trackUserEvent('pattern_products_add_all_to_cart', {
    currency: window.Shopify.currency.active,
    items,
    value: items.reduce(
      (sum, item) => sum + item.price * (item.quantity || 0),
      0
    ),
    action: 'pattern_products_add_all_to_cart',
    category: 'all_recommendations',
    label: patternId
  });
};

const addAllToCart = async () => {
  isAddingAllToCart.value = true;

  try {
    const items = productCardsRef.value
      .map((product) => product.getCartItem())
      .filter((a) => a);
    await cartStore.addIndependentProductsToCart(items);

    trackAddAllToCart(
      productCardsRef.value
        .map((product) => product.getTrackingItem())
        .filter((a) => a)
    );
  } catch (err) {
    const errorMessage = getErrorMessage(err);

    if (errorMessage) {
      buttonAddAllToCartRef?.value?.displayError(errorMessage);
    }
  } finally {
    isAddingAllToCart.value = false;
    displayWarning.value = false;
  }
};

const handleAddAllToCartClick = async () => {
  if (isThereAnyInsufficientStock.value) {
    displayWarning.value = true;
    return;
  }

  addAllToCart();
};

onMounted(() => {
  trackUserEvent('feature_view', {
    action: 'feature_view',
    category: 'section',
    item_name: 'pattern_product_suggestions',
    label: 'Pattern Products'
  });
});
</script>

<template>
  <Transition name="fade" mode="out-in">
    <div
      v-if="hydratedPatternProductsList.length"
      class="mb-6 flex flex-col gap-4 md:inline-flex md:flex-row md:items-center"
    >
      <DropdownSelector
        :options="filterTypeOptions"
        :active="filterTypeOptions[0]"
        :heading="t('patterns.products.type-filter')"
        :show-header="false"
        design="detached"
        @option-click="handleProductTypeChange"
      />

      <div>{{ t('patterns.products.for-size') }}</div>

      <DropdownSelector
        :options="sizeOptions"
        :active="sizeOptions[0]"
        :heading="t('patterns.products.size-filter')"
        :show-header="false"
        design="detached"
        @option-click="handleSizeChange"
      />

      <div class="relative ml-auto w-full md:w-auto">
        <ButtonAddToCart
          ref="ButtonAddAllToCart"
          :disabled="isEverythingOutOfStock"
          :adding="isAddingAllToCart"
          :label="addAllToCartLabel"
          :allow-label-wrap="true"
          @click="handleAddAllToCartClick"
        />
      </div>
    </div>

    <PatternProductSkeleton v-else design="tools" />
  </Transition>

  <div class="flex flex-col gap-6 md:w-full">
    <div class="w-full">
      <Transition name="fade" mode="out-in">
        <div v-if="hydratedPatternProductsList.length">
          <PatternProductCard
            v-for="(patternProduct, i) in hydratedPatternProductsList"
            :key="patternProduct.product_id"
            ref="productCardsRef"
            :index="i"
            :pattern-product="patternProduct"
            @change="onProductChange"
          />
        </div>
        <div v-else>
          <div class="min-h-lvh">
            <PatternProductSkeleton
              v-for="(_, index) in 4"
              :key="`product-skeleton-${index}`"
              design="product"
            />
          </div>
        </div>
      </Transition>
    </div>
  </div>

  <Modal v-model:active="displayWarning" size="md" :always-on-top="true">
    <div
      class="mx-auto flex min-h-96 w-full max-w-2xl flex-col items-center justify-center gap-8 pt-6"
    >
      <h1 class="text-3xl font-bold">{{ t('global.errors.oh-no') }}</h1>

      <div class="mx-4 text-center text-lg text-gray-500">
        {{ t('patterns.products.error.some-out-of-stock-items') }}
      </div>

      <div class="flex flex-col gap-1">
        <ButtonText
          class="mt-4 text-white"
          theme="primary-pink"
          size="lg"
          @click="addAllToCart"
        >
          {{ t('patterns.products.error.proceed') }}
        </ButtonText>
        <ButtonText
          class="mt-4 border font-bold"
          theme="white"
          size="lg"
          @click="displayWarning = false"
        >
          {{ t('patterns.products.error.cancel') }}
        </ButtonText>
      </div>
    </div>
  </Modal>
</template>
